import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import {
  appThemeApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import SettingsIcon from '@material-ui/icons/Settings';
import { MoonIcon, SettingsLineIcon, SunIcon } from './barIcons';

/**
 * The appearance toggle and the signed-in person, in the top bar (DX-275).
 *
 * The other revenexx portals put both here rather than behind a settings page:
 * on revenexx.dev the moon icon and the person sit at the right of the bar, one
 * click each. Backstage buried the appearance in /settings, which is two
 * navigations away from any page a reader is actually on.
 *
 * The settings page stays — it is the only place that shows which catalog
 * entities a reader owns, which is what explains an empty "Components you own"
 * card, and Backstage's sign-out lives there too. What changes is that the two
 * things people reach for daily no longer require going there.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  iconButton: {
    display: 'grid',
    placeItems: 'center',
    width: 32,
    height: 32,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    background: 'none',
    color: 'var(--ui-bar-ink)',
    cursor: 'pointer',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.10)',
      color: '#FFFFFF',
    },
  },
  avatarButton: {
    display: 'flex',
    alignItems: 'center',
    padding: 2,
    border: 'none',
    borderRadius: '50%',
    background: 'none',
    cursor: 'pointer',
  },
  menu: {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 8px)',
    zIndex: theme.zIndex.modal,
    minWidth: '15rem',
    padding: 6,
    borderRadius: 6,
    background: 'var(--ui-bg-elevated)',
    border: '1px solid var(--ui-border)',
    boxShadow: '0 6px 16px -4px rgba(11, 5, 34, 0.24)',
  },
  identity: {
    padding: '8px 10px 10px',
    borderBottom: '1px solid var(--ui-border)',
    marginBottom: 4,
  },
  name: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--ui-text)',
  },
  mail: {
    display: 'block',
    marginTop: 2,
    fontSize: '0.75rem',
    color: 'var(--ui-text-muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    padding: '8px 10px',
    border: 'none',
    borderRadius: 6,
    background: 'none',
    color: 'var(--ui-text)',
    font: 'inherit',
    fontSize: '0.85rem',
    textAlign: 'left',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { background: 'var(--ui-bg-muted)' },
  },
  itemIcon: {
    fontSize: '1.1rem',
    color: 'var(--ui-text-muted)',
  },
}));

export const UserMenu = () => {
  const classes = useStyles();
  const themeApi = useApi(appThemeApiRef);
  const identityApi = useApi(identityApiRef);
  const [open, setOpen] = useState(false);
  const [themeId, setThemeId] = useState(themeApi.getActiveThemeId());
  const [profile, setProfile] = useState<{
    displayName?: string;
    email?: string;
  }>({});
  const root = useRef<HTMLDivElement>(null);

  // The theme can also change from the settings page, so this follows the API
  // rather than keeping a copy of its own.
  useEffect(() => {
    const sub = themeApi.activeThemeId$().subscribe(id => setThemeId(id));
    return () => sub.unsubscribe();
  }, [themeApi]);

  useEffect(() => {
    let cancelled = false;
    identityApi
      .getProfileInfo()
      .then(p => {
        if (!cancelled) {
          setProfile(p);
        }
      })
      .catch(() => {
        // A missing profile is not worth an error here: the menu still offers
        // the appearance toggle and the way out.
      });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onClick = (e: MouseEvent) => {
      if (
        root.current &&
        e.target instanceof Node &&
        !root.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /*
    `themeId` is undefined until someone picks a theme, and Backstage then
    resolves "auto" against the operating system. Assuming light in that case
    made the button lie on a machine set to dark — it offered "Dark mode" while
    already dark, and the click appeared to do nothing. Asking the document
    which mode is actually rendered is the only answer that holds for both.
  */
  const isDark =
    themeId === 'revenexx-dark' ||
    (themeId === undefined &&
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  const toggleTheme = () =>
    themeApi.setActiveThemeId(isDark ? 'revenexx-light' : 'revenexx-dark');

  return (
    <div className={classes.root} ref={root}>
      <button
        type="button"
        className={classes.iconButton}
        onClick={toggleTheme}
        aria-label={isDark ? 'Light mode' : 'Dark mode'}
        title={isDark ? 'Light mode' : 'Dark mode'}
      >
        {isDark ? <SunIcon /> : <MoonIcon />}
      </button>

      {/*
        The settings link sits in the bar rather than only inside the menu.
        It is how a reader reaches the one page that says which catalog
        entities they own — and `signedIn()` in the e2e harness waits for it
        as its proof that the app shell is up, so hiding it behind a menu
        takes the whole suite down with it.
      */}
      <Link
        to="/settings"
        className={classes.iconButton}
        aria-label="Settings"
        title="Settings"
        underline="none"
      >
        <SettingsLineIcon />
      </Link>

      <button
        type="button"
        className={classes.avatarButton}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Konto"
        onClick={e => {
          e.stopPropagation();
          setOpen(v => !v);
        }}
      >
        <UserSettingsSignInAvatar />
      </button>

      {open && (
        <div className={classes.menu} role="menu">
          {(profile.displayName || profile.email) && (
            <div className={classes.identity}>
              {profile.displayName && (
                <span className={classes.name}>{profile.displayName}</span>
              )}
              {profile.email && (
                <span className={classes.mail}>{profile.email}</span>
              )}
            </div>
          )}

          <Link
            to="/settings"
            className={classes.item}
            underline="none"
            onClick={() => setOpen(false)}
            role="menuitem"
          >
            <SettingsIcon className={classes.itemIcon} />
            Einstellungen
          </Link>

          <button
            type="button"
            className={classes.item}
            onClick={() => {
              setOpen(false);
              identityApi.signOut();
            }}
            role="menuitem"
          >
            <ExitToAppIcon className={classes.itemIcon} />
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
};
